import {$} from '../util/view'
import CMPError from '../cmp-error'
import API, {
  Callback, Command, Parameter, ReturnValue, SimpleCallback, QueueCommand
} from './api'
import IFrontendAPI, {requiresView} from './frontend-interface'
import {randomId} from '../util/string'
import {AppView} from '../view/app'
import {onContentLoaded} from '../util/dom'
import debug, {getLevelText, DEBUG, WARN} from '../util/debug'
import {searchParams} from '../util/location'
import {getCurrentPageChannel} from '../util/channel'

interface CMPReturn {
  callId: string,
  returnValue: ReturnValue,
  success: boolean
}

const isFrontendOnlyCommand = (_: FrontendAPI, propertyKey: string) => {
  FrontendAPI.frontendOnlyCommands.push(propertyKey)
}

export default class FrontendAPI extends API implements IFrontendAPI {
  public static frontendOnlyCommands: string[] = []

  public static load (baseURL: string): Promise<FrontendAPI> {
    const {document: doc} = window

    const getIframeURL = (): string => {
      let url = `${baseURL}/html/iframe.html?mol.ads.cmp.log.level=${getLevelText()}`

      if (searchParams['mol.ads.cmp.stubConsent']) {
        url += '&mol.ads.cmp.stubConsent'
      } else if (searchParams['mol.ads.cmp.stubDissent']) {
        url += '&mol.ads.cmp.stubDissent'
      }

      const channel = getCurrentPageChannel()

      if (channel) {
        url += `&mol.ads.cmp.channel=${channel}`
      }

      return url
    }

    const iframe = $('iframe', {
      id: 'mol-ads-cmp-iframe',
      name: '__cmpLocator',
      src: getIframeURL(),
      style: 'width:0;height:0;border:none;',
      title: 'MOL CMP iframe'
    }, [], doc).raw as HTMLIFrameElement

    return new Promise((resolve, reject) => {
      const onLoad = async () => {
        const {contentWindow} = iframe

        iframe.removeEventListener('load', onLoad)

        if (!contentWindow) {
          reject(new CMPError('Couldn\'t successfully create CMP'))
        }

        const api = new this(baseURL, iframe, contentWindow!)
        resolve(api)
      }

      iframe.addEventListener('load', onLoad)
      iframe.addEventListener('error', reject)

      onContentLoaded(() => doc.body.appendChild(iframe))
    })
  }

  public view?: AppView

  protected constructor (
    baseURL: string,
    private iframe: HTMLIFrameElement,
    private iframeWindow: Window
  ) {
    super(baseURL)
  }

  public call (command: Command, parameter: Parameter = null, callback: Callback = () => {}) {
    debug(`calling __cmp(\'${command}\', '${parameter}')`, DEBUG)

    if (FrontendAPI.frontendOnlyCommands.includes(command)) {
      // @ts-ignore
      return this[command](parameter, callback)
    }

    const callId = randomId()

    this.createResponse(callId, (_, data) => {
      debug(`[${callId}] received`)
      debug(data)
      // TODO: Figure out why TS hates this line
      // @ts-ignore
      callback(data.returnValue, data.success)
    })

    debug(`[${callId}] calling ${command}(${parameter})`)
    this.iframeWindow.postMessage({
      __cmpCall: {
        callId,
        command,
        parameter
      }
    }, '*')
  }

  public consentToAll (origin: string = location.origin) {
    this.call('consentToAll', origin, () => {})
  }

  public dissentToAll (origin: string = location.origin) {
    this.call('dissentToAll', origin, () => {})
  }

  @isFrontendOnlyCommand
  @requiresView
  public openSettings (_: null, callback: SimpleCallback) {
    this.view!.openSettings()
    callback(null, true)
  }

  @isFrontendOnlyCommand
  @requiresView
  public giveThanks (_: null, callback: SimpleCallback) {
    this.view!.alert()
    callback(null, true)
  }

  @isFrontendOnlyCommand
  public rendered (_: null, callback: SimpleCallback, count: number = 0) {
    if (this.view) {
      callback(null, true)
    } else if (count > 10) {
      callback(null, false)
    } else {
      setTimeout(() => this.rendered(_, callback, count + 1), 10)
    }
  }

  public unload () {
    const {__cmp: cmp} = window

    delete window.__cmp
    API.provision()

    for (const queueCommand of cmp.a) {
      window.__cmp.a.push(queueCommand)
    }

    this.iframe.remove()
  }

  private createResponse (
    callId: string,
    fn: (data: any, parsedData: CMPReturn) => void
  ) {
    const handler = ({data, source}: MessageEvent) => {
      if (source !== this.iframeWindow) {
        debug('message received from non CMP window', WARN)
        // return
      }

      let parsedData: {__cmpReturn: CMPReturn}

      try {
        parsedData = typeof data === 'string' ? JSON.parse(data) : data
      } catch (error) {
        debug(error, WARN)
        return
      }

      if (parsedData.__cmpReturn && parsedData.__cmpReturn.callId === callId) {
        window.removeEventListener('message', handler)
        fn(data, parsedData.__cmpReturn)
      }
    }

    window.addEventListener('message', handler)
  }
}
